import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CdwRoleEditComponent, CdwRoleEditModule } from '@cdw-ae/cdw-role-edit';
import {
  AccessManagementPermissionAPIReturnFormats,
  AccessManagementPermissionTreeNode,
  AccessManagementRolePermissionRecord,
  AccessManagementRoleRecord,
} from '@cdw-ae/common-data';
import { MatDialog } from '@cdw-ae/common-frontend-materials';
import {
  CdwConfirmationDialogComponent,
  CdwConfirmationDialogData,
  CdwDialogModule,
  ConfirmationDialogResult,
} from '@cdw-ae/common-ui-components-dialog';
import { ErrorSnackbarComponent } from '@cdw-ae/components-error-snackbar';
import { firstValueFrom, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { AccessManagementFrontendAuthService } from '../../access-management-frontend-auth.service';
import { PermissionsDataService } from '../../permissions/permissions-data.service';
import { RolesDataService } from '../roles-data.service';

@Component({
  selector: 'role-edit',
  templateUrl: './role-edit.component.html',
  styleUrls: ['./role-edit.component.scss'],
  imports: [CdwRoleEditModule, CommonModule, CdwDialogModule],
})
export class RoleEditComponent {
  @ViewChild('roleEdit') public roleEditComponent!: CdwRoleEditComponent;
  public currentRolePermissions!: AccessManagementRolePermissionRecord[];
  public currentRole!: AccessManagementRoleRecord;
  public selectedRoleId!: number | 'new';
  public isLoading = false;
  public permissionSelectOptions!: AccessManagementPermissionTreeNode[];
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private accessManagementAuthService: AccessManagementFrontendAuthService,
    private permissionsDataService: PermissionsDataService,
    private roleDataService: RolesDataService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private snackbar: MatSnackBar
  ) {}

  private getCustomerId() {
    return this.accessManagementAuthService.authContext?.sqlCustomerId ?? null;
  }

  public async ngOnInit(): Promise<void> {
    this.isLoading = true;

    await this.accessManagementAuthService.setUserAuthContext();

    const customerId = this.getCustomerId();
    if (!customerId) {
      this.snackbar.openFromComponent(ErrorSnackbarComponent, {
        data: 'Authentication context is not available.',
      });
      this.isLoading = false;
      return;
    }

    this.permissionSelectOptions = (await firstValueFrom(
      this.permissionsDataService.getPermissions(
        AccessManagementPermissionAPIReturnFormats.TREE
      )
    )) as AccessManagementPermissionTreeNode[];

    this.activatedRoute.paramMap
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((paramMap: ParamMap): void => {
        if (paramMap.get('id') && paramMap.get('id') !== 'new') {
          this.selectedRoleId = parseInt(paramMap.get('id') ?? '', 10);
          this.roleDataService
            .getRoleById(this.selectedRoleId)
            .pipe(take(1))
            .subscribe((role) => {
              this.currentRole = role.data.role;
              this.currentRolePermissions = role.data.rolePermissions;
            });
        } else {
          if (this.currentRole) {
            this.roleEditComponent.role = undefined;
            this.roleEditComponent.roleForm.reset();
          }
          this.selectedRoleId = 'new';
        }
        this.isLoading = false;
      });
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public async onSubmitRoleEdit(event: Event): Promise<void> {
    const customerId = this.getCustomerId();
    if (!customerId) {
      this.snackbar.openFromComponent(ErrorSnackbarComponent, {
        data: 'Authentication context is not available.',
      });
      return;
    }

    const roleData = event as unknown as {
      name: string;
      description: string;
      permissions: { [key: string]: boolean };
    };

    const permissions: number[] = Object.keys(roleData.permissions)
      .filter((key) => roleData.permissions[key])
      .map((key) => parseInt(key, 10));

    // Check for duplicate role name (case-insensitive)
    const existingRolesResponse = await firstValueFrom(
      this.roleDataService.getRoles()
    );

    if (existingRolesResponse?.data && Array.isArray(existingRolesResponse.data)) {
      const duplicateRole = existingRolesResponse.data.find(
        (role) =>
          role.name.toLowerCase() === roleData.name.toLowerCase() &&
          role.id !== this.selectedRoleId
      );

      if (duplicateRole) {
        this.snackbar.openFromComponent(ErrorSnackbarComponent, {
          data: `A role with the name "${roleData.name}" already exists. Please choose a different name.`,
        });
        return;
      }
    }

    if (this.selectedRoleId && this.selectedRoleId !== 'new') {
      await this.roleDataService
        .updateRole(
          {
            name: roleData.name,
            description: roleData.description,
            customerId: customerId,
          },
          permissions,
          this.selectedRoleId,
          customerId
        )
        .then((val) => {
          val
            .pipe(take(1))
            .subscribe(async (response: { success: boolean }) => {
              if (response.success) {
                this.snackbar.open('Role updated successfully.', 'OK', { duration: 6000 });
                // Mark form as pristine to prevent "unsaved changes" dialog on navigation
                this.roleEditComponent.roleForm.markAsPristine();
                await this.router.navigate([
                  '/administration/access-management',
                ]);
              } else {
                this.snackbar.openFromComponent(ErrorSnackbarComponent, {
                  data: 'An unexpected error occurred trying to update the role',
                });
              }
            });
        });
    } else {
      await this.roleDataService
        .addRole(
          {
            name: roleData.name,
            description: roleData.description,
            customerId: customerId,
          },
          permissions
        )
        .pipe(take(1))
        .subscribe(async (response) => {
          if (response.success) {
            this.snackbar.open('Role created successfully.', 'OK', { duration: 6000 });
            // Mark form as pristine to prevent "unsaved changes" dialog on navigation
            this.roleEditComponent.roleForm.markAsPristine();
            await this.router.navigate(['/administration/access-management']);
          } else {
            this.snackbar.openFromComponent(ErrorSnackbarComponent, {
              data: 'An unexpected error occurred trying to create the role',
            });
          }
        });
    }
  }

  public async onCancelRoleEdit(): Promise<void> {
    await this.router.navigate(['/administration/access-management']);
  }

  public onDeleteRoleEdit(): void {
    const customerId = this.getCustomerId();
    if (!customerId) {
      this.snackbar.openFromComponent(ErrorSnackbarComponent, {
        data: 'Authentication context is not available.',
      });
      return;
    }

    if (!this.selectedRoleId || this.selectedRoleId === 'new') {
      return;
    }

    // Check if role can be deleted before showing confirmation dialog
    this.roleDataService.canDeleteRole(this.selectedRoleId)
      .pipe(take(1))
      .subscribe((canDeleteResponse) => {
        if (!canDeleteResponse.canDelete) {
          // Show error immediately without confirmation dialog
          this.snackbar.openFromComponent(ErrorSnackbarComponent, {
            data: canDeleteResponse.message || 'Error deleting role, it is assigned to a designation',
          });
          return;
        }

        // Role can be deleted, show confirmation dialog
        const deleteDialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
          data: {
            title: 'Confirmation',
            message: 'Are you sure you want to delete this role?',
            type: 'warning',
            layout: 'warning-box',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            confirmButtonColor: 'warn',
            hard: true,
          },
          width: '540px',
          maxWidth: '100%',
          disableClose: true,
          hasBackdrop: true,
        });

        deleteDialogRef
          .afterClosed()
          .pipe(take(1))
          .subscribe(async (result: ConfirmationDialogResult | undefined) => {
            if (
              result?.confirmed &&
              this.selectedRoleId &&
              this.selectedRoleId !== 'new'
            ) {
              (await this.roleDataService.deleteRole(this.selectedRoleId))
                .pipe(take(1))
                .subscribe(async (response) => {
                  if (response.success) {
                    this.snackbar.open('Role deleted successfully.', 'OK', { duration: 6000 });
                    await this.router.navigate([
                      '/administration/access-management',
                    ]);
                  } else {
                    this.snackbar.openFromComponent(ErrorSnackbarComponent, {
                      data: 'Error deleting role, it is assigned to a designation',
                    });
                  }
                });
            }
          });
      });
  }

  public getTitleText(): string {
    if (this.selectedRoleId !== 'new' && this.currentRole) {
      return `Edit Role: ${this.currentRole.name}`;
    }
    return 'Add New Role';
  }

  /**
   * Confirms navigation when user has unsaved changes
   * Shows a warning dialog to prevent accidental data loss
   * @returns Observable<boolean> - true if navigation allowed, false if blocked
   */
  confirmNavigationWithUnsavedChanges(): Observable<boolean> | boolean {
    // Check if form is dirty (has unsaved changes)
    if (!this.roleEditComponent?.roleForm?.dirty) {
      return true;
    }

    // Show confirmation dialog to warn user about data loss
    const dialogData: CdwConfirmationDialogData = {
      title: 'Confirmation',
      message:
        'You have unsaved changes. Are you sure you would like to continue?',
      type: 'warning',
      size: 'medium',
      layout: 'warning-box',
      fontFamily: 'Roboto',
      confirmText: 'Leave Page',
      cancelText: 'Stay on Page',
      confirmButtonColor: 'warn',
      showCancelButton: true,
      hard: true,
    };

    const dialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true, // Prevent Escape key and backdrop click from closing
    });

    // Return observable that resolves based on user's choice
    return dialogRef.afterClosed().pipe(
      map((result: { confirmed: boolean; dontShowAgain?: boolean }) => {
        return result?.confirmed ?? false;
      })
    );
  }
}
